import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { fontStack } from '../styles/shared';
import { useToast } from '../contexts/ToastContext';

const ACCENT = '#2F6BFF';

const PayoutSection = () => {
    const toast = useToast();
    const [balance, setBalance] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [walletInput, setWalletInput] = useState('');
    const [passwordInput, setPasswordInput] = useState('');
    const [savingWallet, setSavingWallet] = useState(false);
    const [editingWallet, setEditingWallet] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [requesting, setRequesting] = useState(false);

    const loadData = useCallback(async () => {
        try {
            const [balRes, histRes] = await Promise.all([
                api.get('/api/dashboard/payout-balance'),
                api.get('/api/dashboard/payout-history'),
            ]);
            setBalance(balRes.data);
            setHistory(histRes.data.history || []);
        } catch { /* silent */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const handleSaveWallet = async () => {
        if (!walletInput.trim()) return;
        if (!/^0x[0-9a-fA-F]{40}$/.test(walletInput.trim())) {
            toast.error('Invalid USDT wallet address');
            return;
        }
        setSavingWallet(true);
        try {
            await api.post('/api/dashboard/save-wallet', {
                wallet_address: walletInput.trim(),
                password: passwordInput,
            });
            toast.success('Wallet saved');
            setEditingWallet(false);
            setPasswordInput('');
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.error || 'Failed to save wallet');
        } finally { setSavingWallet(false); }
    };

    const handleRequestPayout = async () => {
        setRequesting(true);
        try {
            const res = await api.post('/api/dashboard/request-bsc-payout');
            toast.success(`Payout of $${res.data.amount} requested`);
            setShowConfirm(false);
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.error || 'Failed to request payout');
        } finally { setRequesting(false); }
    };

    const maskWallet = (addr) => addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '';

    const getDisabledReason = () => {
        if (!balance) return null;
        if (!balance.wallet_address) return 'Set a wallet address first';
        if (balance.cooldown_active) return `Wallet cooldown: ${balance.cooldown_hours_left}h remaining`;
        if (balance.available < (balance.min_payout || 20)) return `Minimum payout is $${balance.min_payout || 20}`;
        if (balance.has_pending_request) return 'You have a pending request';
        return null;
    };

    const disabledReason = getDisabledReason();

    if (loading) return <div style={{ padding: 24, color: 'var(--text-secondary)' }}>Loading payout info...</div>;

    const card = (label, value, color) => (
        <div style={s.card}>
            <div style={s.cardLabel}>{label}</div>
            <div style={{ ...s.cardValue, color }}>${(value || 0).toFixed(2)}</div>
        </div>
    );

    const statusPill = (status) => {
        const isSent = status === 'sent';
        const isRejected = status === 'rejected';
        const label = isSent ? 'Paid' : isRejected ? 'Rejected' : 'Pending';
        const color = isSent ? '#10b981' : isRejected ? '#ef4444' : '#eab308';
        const bg = isSent ? 'rgba(16,185,129,0.12)' : isRejected ? 'rgba(239,68,68,0.12)' : 'rgba(234,179,8,0.12)';
        return (
            <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, color, background: bg }}>
                {label}
            </span>
        );
    };

    return (
        <div style={{ fontFamily: fontStack }}>
            {/* ── Balance Cards + Request Payout ── */}
            <div style={s.cardGrid}>
                {card('Available Balance', balance?.available, '#10b981')}
                {card('On Hold (7d)', balance?.holding || 0, '#f59e0b')}
                {card('Pending Payout', balance?.pending_payout, '#3b82f6')}
                {card('Total Earned', balance?.total_earned, 'var(--text-primary)')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 16, lineHeight: 1.5 }}>
                You receive 85% of each sale. Earnings are on hold for 7 days before becoming available for withdrawal.
                {(balance?.gross_total > 0) && (
                    <span> Fees deducted: ${(balance.platform_fee || 0).toFixed(2)} platform (15%) + ${(balance.processing_fee || 0).toFixed(2)} processing (2.7% + $0.30 per transaction) = <strong>${((balance.platform_fee || 0) + (balance.processing_fee || 0)).toFixed(2)}</strong> total from ${(balance.gross_total || 0).toFixed(2)} gross.</span>
                )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                <button
                    onClick={() => setShowConfirm(true)}
                    disabled={!!disabledReason}
                    style={{ ...s.payoutBtn, opacity: disabledReason ? 0.45 : 1, cursor: disabledReason ? 'not-allowed' : 'pointer' }}
                    title={disabledReason || ''}
                >
                    Request Payout
                </button>
                {disabledReason && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{disabledReason}</span>}
            </div>

            {/* ── Wallet Section ── */}
            <div style={s.walletCard}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>USDT Wallet Address (BEP-20)</h3>
                    {balance?.wallet_address && !editingWallet && (
                        <button onClick={() => { setEditingWallet(true); setWalletInput(balance.wallet_address); }} style={s.changeBtn}>Change</button>
                    )}
                </div>

                {balance?.wallet_address && !editingWallet ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <code style={s.walletDisplay}>{maskWallet(balance.wallet_address)}</code>
                        {balance.cooldown_active && (
                            <span style={{ fontSize: 11, color: '#f59e0b' }}>Cooldown: {balance.cooldown_hours_left}h</span>
                        )}
                        {balance.wallet_changed_at && !balance.cooldown_active && (
                            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                                Changed {new Date(balance.wallet_changed_at).toLocaleDateString()}
                            </span>
                        )}
                    </div>
                ) : (
                    <div>
                        <input
                            value={walletInput}
                            onChange={e => setWalletInput(e.target.value)}
                            placeholder="0x..."
                            style={s.input}
                        />
                        {balance?.wallet_address && (
                            <input
                                type="password"
                                value={passwordInput}
                                onChange={e => setPasswordInput(e.target.value)}
                                placeholder="Password to confirm change"
                                style={{ ...s.input, marginTop: 8 }}
                            />
                        )}
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                            <button onClick={handleSaveWallet} disabled={savingWallet || !walletInput.trim()} style={s.saveBtn}>
                                {savingWallet ? 'Saving...' : 'Save'}
                            </button>
                            {editingWallet && (
                                <button onClick={() => { setEditingWallet(false); setPasswordInput(''); }} style={s.cancelBtn}>Cancel</button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* ── Confirm Modal ── */}
            {showConfirm && (
                <div style={s.overlay} onClick={() => setShowConfirm(false)}>
                    <div style={s.modal} onClick={e => e.stopPropagation()}>
                        <h3 style={{ margin: '0 0 16px', fontSize: 17, fontWeight: 700, color: '#1a1a1a' }}>Confirm Payout</h3>
                        <div style={{ marginBottom: 16, fontSize: 14, color: '#4b5563', lineHeight: 1.8 }}>
                            <div>Amount: <strong style={{ color: '#10b981' }}>${(balance?.available || 0).toFixed(2)}</strong></div>
                            <div>Wallet: <code style={{ fontSize: 13, color: '#1a1a1a' }}>{maskWallet(balance?.wallet_address)}</code></div>
                            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>USDT on BNB Smart Chain (BEP-20)</div>
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={handleRequestPayout} disabled={requesting} style={s.confirmBtn}>
                                {requesting ? 'Requesting...' : 'Confirm'}
                            </button>
                            <button onClick={() => setShowConfirm(false)} style={s.cancelBtn}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Payout History ── */}
            <div style={{ marginTop: 16 }}>
                <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>Payout History</h3>
                {history.length === 0 ? (
                    <div style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: '24px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13, border: '1px solid var(--border)' }}>
                        No payout requests yet
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={s.table}>
                            <thead>
                                <tr>
                                    <th style={s.th}>Date</th>
                                    <th style={s.th}>Amount</th>
                                    <th style={s.th}>Status</th>
                                    <th style={s.th}>TX Hash</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map(h => (
                                    <tr key={h.id}>
                                        <td style={s.td}>{new Date(h.requested_at).toLocaleDateString()}</td>
                                        <td style={s.td}>${(h.amount || 0).toFixed(2)}</td>
                                        <td style={s.td}>{statusPill(h.status)}</td>
                                        <td style={s.td}>
                                            {h.tx_hash ? (
                                                <a href={`https://bscscan.com/tx/${h.tx_hash}`} target="_blank" rel="noopener noreferrer"
                                                   style={{ color: ACCENT, fontSize: 12, fontFamily: 'monospace' }}>
                                                    {h.tx_hash.slice(0, 10)}...
                                                </a>
                                            ) : (h.rejection_reason ? <span style={{ color: '#ef4444', fontSize: 12 }}>{h.rejection_reason}</span> : '\u2014')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

const s = {
    cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 },
    card: { background: 'var(--bg-secondary)', borderRadius: 12, padding: '16px 20px', border: '1px solid var(--border)' },
    cardLabel: { fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4, fontWeight: 500 },
    cardValue: { fontSize: 24, fontWeight: 700 },
    walletCard: { background: 'var(--bg-secondary)', borderRadius: 12, padding: '16px 20px', border: '1px solid var(--border)' },
    walletDisplay: { background: 'var(--bg-primary)', padding: '6px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'monospace', color: 'var(--text-primary)', border: '1px solid var(--border)' },
    changeBtn: { fontSize: 12, color: ACCENT, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0 },
    input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 14, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' },
    saveBtn: { padding: '8px 20px', borderRadius: 8, border: 'none', background: ACCENT, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    cancelBtn: { padding: '8px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' },
    payoutBtn: { padding: '10px 24px', borderRadius: 10, border: 'none', background: ACCENT, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
    modal: { background: '#fff', borderRadius: 16, padding: 28, maxWidth: 400, width: '90%', border: '1px solid #e5e7eb', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' },
    confirmBtn: { padding: '10px 28px', borderRadius: 10, border: 'none', background: ACCENT, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text-tertiary)', fontWeight: 500, fontSize: 12 },
    td: { padding: '10px 12px', borderBottom: '1px solid var(--border-light, var(--border))', color: 'var(--text-primary)' },
};

export default PayoutSection;
